import { ClientConstants } from './../../../../formbird-services/src/lib/constants/ClientConstants';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, InjectionToken, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { ComponentDefinition } from '@formbird/types';
import { ChangedDocumentService } from 'formbird-services';
import { FormbirdInjectorService } from '@services/injector/formbird-injector.service';
import { isEmpty } from 'lodash';
import { debounceTime } from 'rxjs/operators';
import { ComponentSetupService } from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import { AbstractComponent, MyErrorStateMatcher } from '../../../../formbird-sc-shared/src/lib/components/abstract.component';
import { isEqual } from 'lodash';

export const WINDOW_TOKEN = new InjectionToken<Window>('Window location object');

interface PartyComponentDefinition extends ComponentDefinition {
  caption?: string;
  style?: string;
  dropDownList?: any;
  defaultValueParty?: any;
  mandatoryValues?: any;
  mandatoryFields?: any;
}

@Component({
  templateUrl: './party.component.html',
  styleUrls: ['./party.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [{provide: WINDOW_TOKEN, useValue: window}],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PartyComponent extends AbstractComponent<PartyComponentDefinition> implements OnInit {

  formControlFirstName: AbstractControl;
  formControlSalutation: AbstractControl;
  formControlSurname: AbstractControl;

  salutationIdName: any;
  surnameBusinessIdName: any;
  firstNameIdName: any;

  matcherSalutation = new MyErrorStateMatcher();
  matcherFirstName = new MyErrorStateMatcher();
  matcherSurname = new MyErrorStateMatcher();

  salutationInputTitle: any;
  firstNameInputTitle: any;
  surnameInputTitle: any;
  selectedTitleValue: any;

  titleDropDownList: any;

  constructor(
    protected formbirdInjectorService: FormbirdInjectorService,
    protected changedDocumentService: ChangedDocumentService,
    protected cdr: ChangeDetectorRef,
    protected componentSetupService: ComponentSetupService
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdr);
  }

  ngOnInit() {

    this.componentSetupService.setupStandardProperties(this);

    this.initializeFieldValue();

    this.setupControls();

    this.setFieldActivation();

    this.setupChangeDetection();

    // sc-party default value parsing
    if (!this.fieldValue && this.componentDefinition.defaultValueParty) {
      const tempValue = this.componentDefinition.defaultValueParty;
      try {
        if (this.isDefined(tempValue) && !this.isDefined(this.fieldValue)) {
          const parsedDefaultValue2 = JSON.parse(tempValue.split('\'').join('"'));
          this.fieldValue = {
            title: parsedDefaultValue2.title ? parsedDefaultValue2.title : '',
            firstName: parsedDefaultValue2.firstName ? parsedDefaultValue2.firstName : '',
            surname: parsedDefaultValue2.surname ? parsedDefaultValue2.surname : ''
          };
          if (!isEqual(this.formControlSalutation.value, this.fieldValue.title)) {
            this.formControlSalutation.patchValue(this.fieldValue.title, {emitEvent: false});
          }
          if (!isEqual(this.formControlFirstName.value, this.fieldValue.firstName)) {
            this.formControlFirstName.patchValue(this.fieldValue.firstName, {emitEvent: false});
          }
          if (!isEqual(this.formControlSurname.value, this.fieldValue.surname)) {
            this.formControlSurname.patchValue(this.fieldValue.surname, {emitEvent: false});
          }
        }
      } catch (err) {
        const errorMessage = this.componentDefinition.label + '=> ' + 'defaultValueParty: "' +
          tempValue + '" is not valid entry. Sample valid defaultValueParty for sc-party is ' +
          '\'+\'"defaultValueParty": "{\'title\':\'Mr\',\'firstName\':\'Test\',\'surname\':\'Test\'}" ';
        console.log(errorMessage);
      }
    }
    if (!this.fieldValue) {
      this.fieldValue = {
        title: '',
        firstName: '',
        surname: ''
      };
      this.selectedTitleValue = 'Salutation';
    } else {
      this.selectedTitleValue = this.fieldValue.title ? this.fieldValue.title : 'Salutation';
    }

    if (this.isEmptyParty(this.fieldValue)) {

      // should not dispatch the empty field value to store
      this.shouldDispatchEmptyFieldValue = false;

    }
    this.formControlSalutation.markAllAsTouched();
    this.formControlFirstName.markAllAsTouched();
    this.formControlSurname.markAllAsTouched();
  }

  initializeFieldValue() {

    this.salutationIdName = 'salutation' + this.componentDefinition.name;
    this.firstNameIdName = 'firstName' + this.componentDefinition.name;
    this.surnameBusinessIdName = 'surnameBusiness' + this.componentDefinition.name;

    const defaultDropDownList = ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr'];
    if (this.componentDefinition.dropDownList) {
      this.titleDropDownList = this.componentDefinition.dropDownList;
    } else {
      this.titleDropDownList = defaultDropDownList;
    }
  }

  setupChangeDetection() {
    super.setupChangeDetection();

    const firstNameSub = this.formControlFirstName.valueChanges.pipe(debounceTime(ClientConstants.VALIDATION_INTERVAL_TIME))
      .subscribe(value => {
        this.valueChangedParty('firstName', value);
      });

    const surNameSub = this.formControlSurname.valueChanges.pipe(debounceTime(ClientConstants.VALIDATION_INTERVAL_TIME))
      .subscribe(value => {
        this.valueChangedParty('surname', value);
      });

    this.subs.add(firstNameSub);
    this.subs.add(surNameSub);
  }

  onFieldValueChanged(fieldValue) {
    // set default value
    if (!this.fieldValue && !fieldValue && this.componentDefinition.defaultValueParty) {
      const tempValue = this.componentDefinition.defaultValueParty;
      try {
        if (this.isDefined(tempValue) && !this.isDefined(this.fieldValue)) {
          const parsedDefaultValue2 = JSON.parse(tempValue.split('\'').join('"'));
          this.fieldValue = {
            title: parsedDefaultValue2.title ? parsedDefaultValue2.title : '',
            firstName: parsedDefaultValue2.firstName ? parsedDefaultValue2.firstName : '',
            surname: parsedDefaultValue2.surname ? parsedDefaultValue2.surname : ''
          };
          this.setFormValues(this.fieldValue);
        }
      } catch (err) {
        const errorMessage = this.componentDefinition.label + '=> ' + 'defaultValueParty: "' +
          tempValue + '" is not valid entry. Sample valid defaultValueParty for sc-party is ' +
          '\'+\'"defaultValueParty": "{\'title\':\'Mr\',\'firstName\':\'Test\',\'surname\':\'Test\'}" ';
        console.log(errorMessage);
      }
    } else if (!isEqual(this.fieldValue, fieldValue)) {
      this.fieldValue = fieldValue;
      this.setFormValues(fieldValue);

      this.detectChanges();
    }
  }

  setupControls() {

    // mandatory
    let titleValidator;
    let firstNameValidator;
    let surnameValidator;
    if (this.componentDefinition.hasOwnProperty('mandatory')) {
      if (this.componentDefinition.mandatory === true) {

        const validators = [Validators.required];

        const emptyMandatoryFields = typeof this.componentDefinition.mandatoryFields === 'undefined'
          || this.componentDefinition.mandatoryFields === null
          || this.isEmptyObject(this.componentDefinition.mandatoryFields);
        const emptyMandatoryValues = typeof this.componentDefinition.mandatoryValues === 'undefined'
          || this.componentDefinition.mandatoryValues === null
          || this.isEmptyObject(this.componentDefinition.mandatoryValues);

        if (emptyMandatoryFields && emptyMandatoryValues) {

          this.componentDefinition = { ...this.componentDefinition };
          this.componentDefinition.mandatoryFields = ['title', 'firstName', 'surname'];

          // tslint:disable-next-line:max-line-length
          this.changedDocumentService.notifyComponentDefinitionUpdated(this.template.documentId, this.fieldName, this.key, this.componentDefinition);
        }

        for (const mandatoryIndex in this.componentDefinition.mandatoryFields) {

          if (JSON.stringify(this.componentDefinition.mandatoryFields[mandatoryIndex]) === JSON.stringify('title')) {
            titleValidator = validators;

          } else if (JSON.stringify(this.componentDefinition.mandatoryFields[mandatoryIndex]) === JSON.stringify('firstName')) {
            firstNameValidator = validators;

          } else if (JSON.stringify(this.componentDefinition.mandatoryFields[mandatoryIndex]) === JSON.stringify('surname')) {
            surnameValidator = validators;

          }

        }
      }
    }

    const name1 = this.registeredControlName + '_title';
    const value1 = this.fieldValue?.title ? this.fieldValue?.title : '';
    this.formControlSalutation = this.componentSetupService.setupControl(this, name1, value1, titleValidator);

    const name2 = this.registeredControlName + '_firstName';
    const value2 = this.fieldValue?.firstName ? this.fieldValue?.firstName : '';
    this.formControlFirstName = this.componentSetupService.setupControl(this, name2, value2, firstNameValidator);

    const name3 = this.registeredControlName + '_surname';
    const value3 = this.fieldValue?.surname ? this.fieldValue?.surname : '';
    this.formControlSurname = this.componentSetupService.setupControl(this, name3, value3, surnameValidator);
  }

  setFormValues(fieldValue) {

    this.formControlSalutation.patchValue(fieldValue ? fieldValue.title : '', {emitEvent: false});
    this.formControlFirstName.patchValue(fieldValue ? fieldValue.firstName : '', {emitEvent: false});
    this.formControlSurname.patchValue(fieldValue ? fieldValue.surname : '', {emitEvent: false});

    this.selectedTitleValue = fieldValue && fieldValue.title ? fieldValue.title : 'Salutation';

  }

  setFieldActivation() {
    if (this.disabled) {
      this.formControlSalutation.disable({emitEvent: false});
      this.formControlFirstName.disable({emitEvent: false});
      this.formControlSurname.disable({emitEvent: false});
    } else {
      this.formControlSalutation.enable({emitEvent: false});
      this.formControlFirstName.enable({emitEvent: false});
      this.formControlSurname.enable({emitEvent: false});
    }
  }

  valueChangedParty(type, $event) {

    let newValue;

    switch (type) {
      case 'salutation':
        if ($event) {
          this.salutationInputTitle = $event;
        } else {
          this.salutationInputTitle = 'Salutation field is required';
          if (this.testIfFieldMandatory('title')) {
            this.salutationInputTitle = 'Salutation field is required';
          } else {
            this.salutationInputTitle = 'Salutation';
          }
        }
        newValue = {
          title: $event,
          firstName: this.formControlFirstName.value,
          surname: this.formControlSurname.value
        };
        break;
      case 'firstName':
        if ($event) {
          this.firstNameInputTitle = $event;
        } else {
          if (this.testIfFieldMandatory('firstName')) {
            this.firstNameInputTitle = 'FirstName field is required';
          } else {
            this.firstNameInputTitle = 'FirstName';
          }
        }
        newValue = {
          title: this.formControlSalutation.value,
          firstName: $event,
          surname: this.formControlSurname.value
        };
        break;
      case 'surname':
        if ($event) {
          this.surnameInputTitle = $event;
        } else {
          if (this.testIfFieldMandatory('surname')) {
            this.surnameInputTitle = 'Surname field is required';
          } else {
            this.surnameInputTitle = 'Surname';
          }
        }
        newValue = {
          title: this.formControlSalutation.value,
          firstName: this.formControlFirstName.value,
          surname: $event
        };
        break;
    }

    if (this.componentDefinition.mandatory) {
      /*** mandatoryFields code ***/
      /****************************/
      // for (const mandatoryIndex in this.componentDefinition.mandatoryFields) {
      //   if (JSON.stringify(this.componentDefinition.mandatoryFields[mandatoryIndex]) === JSON.stringify('title')) {
      //     if (!this.formControlSalutation.value) {
      //       delete newValue.title;
      //     }
      //   } else if (JSON.stringify(this.componentDefinition.mandatoryFields[mandatoryIndex]) === JSON.stringify('firstName')) {
      //     if (!this.formControlFirstName.value) {
      //       delete newValue.firstName;
      //     }
      //   } else if (JSON.stringify(this.componentDefinition.mandatoryFields[mandatoryIndex]) === JSON.stringify('surname')) {
      //     if (!this.formControlSurname.value) {
      //       delete newValue.surname;
      //     }
      //   }
      // }
      /*** End of mandatoryFields code ***/
      /***********************************/

      /***** MandatoryValues codes ******/
      /**********************************/
      // if (this.componentDefinition.mandatory && !this.isEmptyObject(this.componentDefinition.mandatoryValues)) {
      //   /*firstName */
      //   if (!(this.IsNullOrEmpty(this.componentDefinition.mandatoryValues.firstName))) {
      //     if (!this.formControlFirstName.value) {
      //       delete newValue.firstName;
      //     }
      //   }
      //
      //   /*title */
      //   if (!(this.IsNullOrEmpty(this.componentDefinition.mandatoryValues.title))) {
      //     if (!this.formControlSalutation.value) {
      //       delete newValue.title;
      //     }
      //   }
      //
      //   /*surname */
      //   if (!(this.IsNullOrEmpty(this.componentDefinition.mandatoryValues.surname))) {
      //     if (!this.formControlSurname.value) {
      //       delete newValue.surname;
      //     }
      //   }
      // }
      /*** End of MandatoryValues code ***/
      /***********************************/
      if (JSON.stringify(newValue) === '{"title":"","firstName":"","surname":""}') {
        delete newValue.title;
        delete newValue.firstName;
        delete newValue.surname;
      }
    } else {
      if (!this.formControlSalutation.value) {
        delete newValue.title;
      }
      if (!this.formControlFirstName.value) {
        delete newValue.firstName;
      }
      if (!this.formControlSurname.value) {
        delete newValue.surname;
      }

    }

    if (this.isEmptyParty(newValue)) {
      newValue = undefined;
    }

    this.valueChanged(newValue);

  }


  isDefined(value) {
    return typeof value !== 'undefined';
  }

  isEmptyObject(obj) {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        return false;
      }
    }
    return true;
  }

  isEmptyParty(value) {
    const empty = isEmpty(value);
    if (empty) {
      return true;
    }

    let res = true;
    for (const key in value) {
      if (value[key]) {
        res = false;
        break;
      }
    }
    return res;

  }

  testIfFieldMandatory(key) {
    let returnVal = false;
    if (this.componentDefinition.mandatory) {
      for (const keyTpl in this.componentDefinition.mandatoryFields) {
        if ((JSON.stringify(this.componentDefinition.mandatoryFields[keyTpl]) === JSON.stringify(key))) {
          returnVal = true;
          break;
        }
      }
    } else {
      delete this.componentDefinition.mandatoryFields;
    }
    return returnVal;
  }

  valueChanged(value) {
    super.valueChanged(value);

    if (!this.selectedTitleValue) {
      this.selectedTitleValue = 'Salutation';
    }
  }

  trackByFn(index, item) {
    return item;
  }

  showToolTip(tooltip: MatTooltip, formFieldContainer: MatFormField, labelContainer: HTMLElement) {
    return (labelContainer.offsetWidth <= formFieldContainer._elementRef.nativeElement.scrollWidth);
  }

  toggleTooltip(tooltip: MatTooltip) {
    setTimeout(() => {
      tooltip.toggle();
    });
  }
}

